import { textBlockMargins } from "./fonts";
import { snap } from "./units";
import { fills, type BoxNode, type Node, type PageSpec, type TextNode } from "./ir";

// Page splitting, done once for both renderers. A template's flow (or its two
// partition columns) is laid out at full size in the browser, every block is
// measured, and the blocks are distributed over pages here. The on-screen
// preview and the PDF then both draw exactly these page fragments — react-pdf
// never paginates on its own (every PDF page is a single, non-wrapping page)
// — so the PDF breaks pages exactly where the preview shows them.
//
// Rules (the app's MultiPage semantics, plus typographic clean-up):
// - Blocks are atomic: text never splits across pages, and keep() groups (a
//   heading with its first entry, an entry header with its first line) move
//   to the next page whole. Only plain column boxes holding several blocks
//   split, between their children; a split box loses its top padding on the
//   continuing page(s) and its bottom padding on the page(s) before.
// - Empty space never starts a page: spacers that would sit at the top of a
//   continuation page are dropped, and a spacer that does not fit at the end
//   of a page is consumed by the break.
// - A block taller than a whole page gets a page of its own (and is clipped).
// - Partition columns (sidebar layouts) paginate independently, like the
//   app's pw.Partitions; the document has as many pages as the longer one.

/** What is on one page: the body blocks, or the two partition columns. */
export type PageFrame = { body?: Node[]; left?: Node[]; right?: Node[] };

export type PagedDoc = {
  spec: PageSpec;
  pages: PageFrame[];
};

/** A measured atomic block of a flow, in flow coordinates (pt). */
type Item = {
  path: string;
  /** Margin-box top/bottom of the block itself. */
  top: number;
  bottom: number;
  /** Top including every splittable ancestor that opens at this block. */
  openStart: number;
  /** Bottom including every splittable ancestor that closes at this block. */
  closeEnd: number;
  blank: boolean;
};

/** Measured facts about a splittable box. */
type BoxInfo = { first: string; last: string; width: number };

export type FlowMeasurement = { items: Item[]; boxes: Map<string, BoxInfo> };

const EPS = 0.01;
const PX_TO_PT = 72 / 96;

function isAbsolute(n: Node): boolean {
  return n.kind === "box" && n.style.position === "absolute";
}

/**
 * Whether a box may split across pages: a plain column holding more than one
 * flowing block (or one splittable block). Rows, keep() groups, fixed-height
 * boxes and anything with a visible background or border stay whole.
 */
export function splittable(n: Node): n is BoxNode {
  if (n.kind !== "box" || n.keep || (n.style.dir ?? "column") === "row") return false;
  const s = n.style;
  if (s.position === "absolute" || s.height !== undefined || s.bg || s.border || s.borderLeft || s.borderBottom) return false;
  const flowing = n.children.filter((c) => !isAbsolute(c));
  return flowing.length > 1 || (flowing.length === 1 && splittable(flowing[0]));
}

/** Empty space only: spacers, or plain boxes holding nothing but spacers. */
function blank(n: Node): boolean {
  if (n.kind !== "box") return false;
  const s = n.style;
  if (s.bg || s.border || s.borderLeft || s.borderBottom || s.width !== undefined) return false;
  return n.children.every(blank);
}

/** Vertical margins of a node as the HTML renderer lays it out (pt). */
function verticalMargins(n: Node): [number, number] {
  if (n.kind === "text") return textBlockMargins(n.runs, n.lineSpacing);
  if (n.kind === "box" && n.style.margin) return [n.style.margin[0], n.style.margin[2]];
  return [0, 0];
}

/** The element of each child of a rendered box (border overlays skipped). */
function childElements(el: Element): Element[] {
  return Array.from(el.children).filter((c) => !(c as HTMLElement).hasAttribute("data-ov"));
}

/**
 * Measures a flow rendered by {@link FlowView}: [container]'s element
 * children are the flow's blocks, in order.
 */
export function measureFlow(container: HTMLElement, nodes: Node[]): FlowMeasurement {
  const origin = container.getBoundingClientRect();
  // Guards against a transformed ancestor (the measurement host is unscaled).
  const scale = container.offsetWidth ? origin.width / container.offsetWidth : 1;
  const toPt = (px: number) => (px / (scale || 1)) * PX_TO_PT;
  const items: Item[] = [];
  const boxes = new Map<string, BoxInfo>();

  const walk = (list: Node[], els: Element[], prefix: string): Item[] => {
    const out: Item[] = [];
    list.forEach((n, i) => {
      if (isAbsolute(n)) return;
      const el = els[i];
      if (!el) return;
      const path = prefix ? `${prefix}.${i}` : `${i}`;
      const r = el.getBoundingClientRect();
      const [mt, mb] = verticalMargins(n);
      const top = toPt(r.top - origin.top) - mt;
      const bottom = toPt(r.bottom - origin.top) + mb;
      if (splittable(n)) {
        const inner = walk(n.children, childElements(el), path);
        if (inner.length) {
          inner[0].openStart = Math.min(inner[0].openStart, top);
          inner[inner.length - 1].closeEnd = Math.max(inner[inner.length - 1].closeEnd, bottom);
          boxes.set(path, { first: inner[0].path, last: inner[inner.length - 1].path, width: toPt(r.width) });
          out.push(...inner);
          return;
        }
      }
      out.push({ path, top, bottom, openStart: top, closeEnd: bottom, blank: blank(n) });
    });
    return out;
  };

  items.push(...walk(nodes, Array.from(container.children), ""));
  return { items, boxes };
}

/** Distributes a flow's items over pages of the given content heights. */
function distribute(items: Item[], height: (page: number) => number): Item[][] {
  const pages: Item[][] = [];
  let cur: Item[] = [];
  let startY = 0;
  const finish = () => {
    pages.push(cur);
    cur = [];
  };
  for (const it of items) {
    const pageIndex = pages.length;
    if (!cur.length && pageIndex > 0 && it.blank) continue;
    if (!cur.length) startY = it.openStart;
    if (it.closeEnd - startY <= height(pageIndex) + EPS) {
      cur.push(it);
      continue;
    }
    if (it.blank) {
      // The break consumes the spacer.
      if (cur.length) finish();
      continue;
    }
    if (cur.length) {
      finish();
      startY = it.openStart;
    }
    // Fits on the fresh page — or is taller than a page and overflows alone.
    cur.push(it);
  }
  if (cur.length || !pages.length) pages.push(cur);
  return pages;
}

/**
 * The blocks of [nodes] that are on one page, with split boxes sliced.
 * Absolutely positioned decorations go with the fragment that holds their
 * parent's first block ([keepAbsolute]).
 */
function fragment(nodes: Node[], prefix: string, onPage: Set<string>, m: FlowMeasurement, keepAbsolute: boolean): Node[] {
  const out: Node[] = [];
  nodes.forEach((n, i) => {
    if (isAbsolute(n)) {
      if (keepAbsolute) out.push(n);
      return;
    }
    const path = prefix ? `${prefix}.${i}` : `${i}`;
    if (onPage.has(path)) {
      out.push(n);
      return;
    }
    const info = m.boxes.get(path);
    if (!info || n.kind !== "box") return;
    const firstHere = onPage.has(info.first);
    const lastHere = onPage.has(info.last);
    const children = fragment(n.children, path, onPage, m, firstHere);
    if (!children.some((c) => !isAbsolute(c))) return;
    const style = { ...n.style };
    if (!firstHere || !lastHere) {
      if (style.pad) style.pad = [firstHere ? style.pad[0] : 0, style.pad[1], lastHere ? style.pad[2] : 0, style.pad[3]];
      if (style.margin) style.margin = [firstHere ? style.margin[0] : 0, style.margin[1], lastHere ? style.margin[2] : 0, style.margin[3]];
      // Keep the unsplit width so nothing inside re-wraps.
      if (!fills(n) && style.width === undefined) style.width = info.width;
    }
    out.push({ ...n, style, children });
  });
  return out;
}

/** Content height of page [index] (page 1 may start lower or higher), on the layout grid. */
export function contentHeight(spec: PageSpec, index: number): number {
  const [top, , bottom] = spec.pad;
  const t = index === 0 ? (spec.firstTop ?? top) : top;
  return snap(spec.size.height) - snap(t) - snap(bottom);
}

/** Width of the content box and of each partition column (pt). */
export function flowWidths(spec: PageSpec): { body: number; left: number; right: number } {
  const body = spec.size.width - spec.pad[1] - spec.pad[3];
  const cols = spec.columns;
  if (!cols) return { body, left: 0, right: 0 };
  const left = cols.leftWidth ?? body - (cols.rightWidth ?? 0);
  return { body, left, right: body - left };
}

/** Splits measured flows into pages. */
export function paginate(
  spec: PageSpec,
  m: { body?: FlowMeasurement; left?: FlowMeasurement; right?: FlowMeasurement }
): PagedDoc {
  const height = (i: number) => contentHeight(spec, i);
  if (spec.columns && m.left && m.right) {
    const lp = distribute(m.left.items, height);
    const rp = distribute(m.right.items, height);
    const count = Math.max(lp.length, rp.length);
    const pages: PageFrame[] = [];
    for (let i = 0; i < count; i++) {
      pages.push({
        left: fragment(spec.columns.left, "", new Set((lp[i] ?? []).map((it) => it.path)), m.left, i === 0),
        right: fragment(spec.columns.right, "", new Set((rp[i] ?? []).map((it) => it.path)), m.right, i === 0),
      });
    }
    return { spec, pages };
  }
  const bm = m.body ?? { items: [], boxes: new Map() };
  const pages = distribute(bm.items, height).map((list, i) => ({
    body: fragment(spec.body, "", new Set(list.map((it) => it.path)), bm, i === 0),
  }));
  return { spec, pages };
}

/** A single page holding everything — used before fonts are ready to measure. */
export function unpaginated(spec: PageSpec): PagedDoc {
  return { spec, pages: [spec.columns ? { left: spec.columns.left, right: spec.columns.right } : { body: spec.body }] };
}

/** Every text node of a node list, depth-first (the order they render in). */
export function collectTextNodes(nodes: Node[], into: TextNode[] = []): TextNode[] {
  for (const n of nodes) {
    if (n.kind === "text") into.push(n);
    else if (n.kind === "box") collectTextNodes(n.children, into);
  }
  return into;
}
